import { getAddress } from '@ethersproject/address'
import { AddressZero } from '@ethersproject/constants'
import { Contract } from '@ethersproject/contracts'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { providers } from 'ethers'
import { Web3ContactType } from 'Modules'
import { useMemo } from 'react'
import type { Account, Chain, Client, Transport } from 'viem'
import { Config, useAccount, useChainId, useConnectorClient } from 'wagmi'
import Web3 from 'web3'
import { AbiItem } from 'web3-utils'

import { WEB3_PROVIDER } from 'constants/index'
import { logError } from 'utils/logger'
// import { WORK_CHAIN } from "hooks/constants";

export function clientToSigner(client: Client<Transport, Chain, Account>) {
  const { chain, transport }: any = client

  if (!chain) {
    return undefined
  }
  const network = {
    chainId: chain?.id,
    name: chain?.name,
    ensAddress: chain?.contracts?.ensRegistry?.address,
  }
  const provider = new providers.Web3Provider(transport, network)
  return provider
}

export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

export function getProviderOrSigner(
  library: Web3Provider,
  account?: string
): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

export function getContract(
  address: string,
  ABI: any,
  library: Web3Provider,
  account?: string
): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return new Contract(address, ABI, getProviderOrSigner(library, account) as any)
}

// returns null on errors
export function useContract(contractAddress: any, ABI: any, withSignerIfPossible = true) {
  const { address } = useAccount()
  const chainId = useChainId()
  const account = address
  const { data: client } = useConnectorClient<Config>({ chainId })
  const library: any = client ? clientToSigner(client) : undefined

  return useMemo(() => {
    if (!contractAddress || !ABI || !library) return null
    try {
      return getContract(
        contractAddress,
        ABI,
        library,
        withSignerIfPossible && account ? account : undefined
      )
    } catch (error) {
      logError('Failed to get contract', error)
      return null
    }
  }, [contractAddress, ABI, library, withSignerIfPossible, account])
}

/**
 * init contract with Web3 JS
 * @param contractAddress
 * @param ABI
 * @returns
 */
export const useContractWeb3 = (contractAddress: any, ABI: any): Web3ContactType => {
  const web3: any = new Web3(WEB3_PROVIDER)
  const contract = useMemo(() => {
    if (!contractAddress || !ABI) return null
    return new web3.eth.Contract(ABI as unknown as AbiItem, contractAddress)
  }, [contractAddress, ABI, web3.eth.Contract])

  return {
    web3,
    contract,
  } as Web3ContactType
}
